import { type FC, type RefObject, useEffect, useState } from "react";

import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  SELECTION_CHANGE_COMMAND,
} from "lexical";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { Button } from "./button";
import { EditorToolbarButtonBold } from "./editor_toolbar_button_bold";
import { EditorToolbarButtonItalic } from "./editor_toolbar_button_italic";
import { EditorToolbarButtonUnderline } from "./editor_toolbar_button_underline";
import { EditorToolbarContext } from "./editor_toolbar_context";
import { EditorToolbarDivider } from "./editor_toolbar_divider";
import { type ParagraphRun } from "./paragraph_editor_runs";
import { readRuns } from "./paragraph_editor_state";

const PLAIN = {
  canUndo: false,
  canRedo: false,
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isStrikethrough: false,
  isCode: false,
};

// B · I · U, lit for the selection, then Done. A press keeps his caret in the
// paragraph.
export const ParagraphFormatBar: FC<{
  saving: boolean;
  closed: RefObject<boolean>;
  onDone(runs: ParagraphRun[]): void;
}> = ({ saving, closed, onDone }) => {
  const [editor] = useLexicalComposerContext();
  const [lit, setLit] = useState(PLAIN);

  // A toggle changes the selection too, so it lights here.
  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            setLit({
              ...PLAIN,
              isBold: selection.hasFormat("bold"),
              isItalic: selection.hasFormat("italic"),
              isUnderline: selection.hasFormat("underline"),
            });
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor],
  );

  return (
    <EditorToolbarContext value={lit}>
      <div
        className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 shadow-md"
        onMouseDown={(event) => event.preventDefault()}
      >
        <EditorToolbarButtonBold />
        <EditorToolbarButtonItalic />
        <EditorToolbarButtonUnderline />
        <EditorToolbarDivider />
        <Button
          type="button"
          color="navy"
          size="sm"
          loading={saving}
          onClick={() => {
            closed.current = true;
            onDone(readRuns(editor));
          }}
        >
          Done
        </Button>
      </div>
    </EditorToolbarContext>
  );
};
